import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleBySlug, articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { useMemo } from 'react';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter(a => a.categorySlug === article.categorySlug && a.slug !== article.slug)
      .slice(0, 3);
  }, [article]);

  const category = article ? categories.find(c => c.slug === article.categorySlug) : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageTitle = `${article.title} — 案件深度分析`;
  const pageDescription = article.excerpt;
  const pageUrl = `https://lizongrui-case-analysis.readdy.ai/article/${article.slug}`;

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <article>
          <div className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
            <img
              src={article.imageUrlHero}
              alt={article.title}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/40"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <div className="max-w-4xl mx-auto">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="inline-block text-xs font-medium text-primary-300 bg-primary-900/50 px-3 py-1 rounded-full mb-4 hover:bg-primary-800/50 transition-colors cursor-pointer"
                  >
                    {category.name}
                  </Link>
                )}
                <h1 className="text-xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-50 leading-tight mb-4">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-300">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.publishedAt}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    阅读约 {article.readTime} 分钟
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
                { label: article.title },
              ]}
            />
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 pb-10 md:pb-14">
            <div
              className="prose prose-lg max-w-none
                prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-bold
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5
                prose-strong:text-foreground-900
                [&_h2]:border-l-4 [&_h2]:border-primary-500 [&_h2]:pl-4"
              dangerouslySetInnerHTML={{ __html: article.content }}
            ></div>

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs font-medium text-foreground-500 bg-secondary-100 px-3 py-1.5 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-12 pt-10 border-t border-background-200/70">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.slug}
                      to={`/article/${rel.slug}`}
                      className="group bg-background-100 rounded-lg border border-background-200/70 hover:border-primary-300/50 transition-all duration-300 overflow-hidden cursor-pointer"
                    >
                      <div className="h-40 overflow-hidden">
                        <img
                          src={rel.imageUrl}
                          alt={rel.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                          {rel.title}
                        </h3>
                        <p className="text-xs text-foreground-500 line-clamp-2">
                          {rel.excerpt}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              description: article.excerpt,
              author: {
                '@type': 'Person',
                name: article.author,
              },
              datePublished: article.publishedAt,
              url: pageUrl,
              keywords: article.tags.join(', '),
              articleBody: article.content.replace(/<[^>]*>/g, ''),
            }),
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://lizongrui-case-analysis.readdy.ai/',
                },
                ...(category
                  ? [
                      {
                        '@type': 'ListItem',
                        position: 2,
                        name: category.name,
                        item: `https://lizongrui-case-analysis.readdy.ai/category/${category.slug}`,
                      },
                      {
                        '@type': 'ListItem',
                        position: 3,
                        name: article.title,
                      },
                    ]
                  : [
                      {
                        '@type': 'ListItem',
                        position: 2,
                        name: article.title,
                      },
                    ]),
              ],
            }),
          }}
        />
      </main>
      <Footer />

      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={article.tags.join(', ')} />
      <link rel="canonical" href={pageUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={pageUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
    </>
  );
}